import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { fadeInOut, slideInOut } from '../../shared/animations';

/**
 * One option in the sheet.
 *
 * `labelKey` rather than a fixed label map on the component: the two callers word the
 * same choice differently on purpose — the bill sheet says "Pay in cash"
 * (`menu.actions.requestBill.modal.cashButton`), the order screen says just "Cash"
 * (`orderDetails.paymentMethod.cash`). Neither is a prefix of the other, so the wording
 * belongs to the caller and only the icon is derived here.
 */
export interface PaymentSheetOption {
  /** Emitted verbatim on tap. The caller decides what it means. */
  method: string;
  /** i18n key, translated inside the sheet. The row's title. */
  labelKey: string;
  /**
   * Rows variant only — the sentence under the title, saying where and when the money
   * actually moves ("Pay in cash at the table"). Omitted renders a title-only row.
   */
  subtitleKey?: string;
  /**
   * Rows variant only — what the commit button says while THIS option is selected.
   *
   * It travels on the option rather than as an @Input because the label depends on which
   * method is highlighted, and `selected` is this component's own state — a parent has
   * nothing to bind to. It also keeps wording with the caller, which is the same reason
   * `labelKey` lives here. Falls back to `confirmLabelKey`.
   */
  commitLabelKey?: string;
}

/**
 * "How are you paying?" as a themed bottom sheet.
 *
 * Extracted from menu.component's request-a-bill sheet so the order screen can use the
 * same one — it previously had a `success-modal` with hardcoded `#4CAF50` / `#ddd`
 * inline styles that ignored the venue's colours entirely.
 *
 * ⚠️ **Presentational only.** It renders options and emits the one tapped. It knows
 * nothing about orders, waiter notifications or BORICA, and it must stay that way —
 * that is what lets two unrelated flows share it.
 */
@Component({
  selector: 'app-payment-method-sheet',
  templateUrl: './payment-method-sheet.component.html',
  styleUrls: ['./payment-method-sheet.component.scss'],
  standalone: false,
  animations: [fadeInOut, slideInOut],
})
export class PaymentMethodSheetComponent implements OnChanges {
  @Input() open = false;
  @Input() options: PaymentSheetOption[] = [];
  @Input() titleKey = '';
  @Input() subtitleKey?: string;

  /**
   * How the options are drawn, and therefore what a tap means.
   *
   *   'artwork' — wide illustrated buttons; a tap IS the choice. Two of these fill the
   *               width comfortably, which is what the request-a-bill sheet has.
   *   'rows'    — a vertical list of title-and-subtitle rows that select, committed by
   *               the footer button. Three artwork buttons stagger and wrap their
   *               labels, and a row that commits on tap contradicts what a selected row
   *               looks like — and this is a screen about money.
   *
   * Defaults to 'artwork' so the bill sheet is unaffected by this input existing.
   */
  @Input() variant: 'artwork' | 'rows' = 'artwork';

  /** Rows only — the commit button's label when the selected option names none. */
  @Input() confirmLabelKey = 'orderDetails.orderNow.button';

  /** Rows only. Pure view state: the caller hears about it when confirm is pressed. */
  selected = '';

  /** Disables every button — the bill sheet holds this while its request is in flight. */
  @Input() busy = false;

  @Output() choose = new EventEmitter<string>();
  @Output() dismiss = new EventEmitter<void>();

  /**
   * Rows only — the guest highlighted a different row. Nothing is ordered by it.
   *
   * For a caller that shows something of its own under the rows depending on what is
   * highlighted (order-details projects the tip picker in while Online payment is). Emitted
   * on TAPS only, not for the preselection when the sheet opens: that happens inside
   * ngOnChanges, mid-way through the parent's change detection, where an emit would change
   * the parent's state after it was checked. The preselection is always the first option,
   * so a caller seeds its own copy from `options[0]` when it opens the sheet.
   */
  @Output() selectedChange = new EventEmitter<string>();

  /**
   * Icons are keyed off the method rather than passed in, because the mapping is a
   * property of what the method IS, not of who is asking.
   *
   * `online_payment` reuses the card artwork deliberately: a BORICA payment is a card
   * payment, and inventing a third icon would imply a distinction the guest does not
   * have to care about at this moment.
   */
  private static readonly ICONS: Record<string, string> = {
    cash_on_delivery: 'pay-cash.svg',
    card_on_delivery: 'pay-card.svg',
    online_payment: 'pay-card.svg',
  };

  /**
   * Runtime path, not a stylesheet `url()`. The original lived in menu.component.scss
   * as `url('../../assets/pay-cash.svg')` — a path relative to that file, which would
   * silently resolve to nothing from this directory. Angular serves `assets/` from the
   * app root, so this form is location-independent and can be asserted in a test.
   */
  iconFor(method: string): string {
    return `assets/${PaymentMethodSheetComponent.ICONS[method] ?? 'pay-card.svg'}`;
  }

  /**
   * Preselect the first option every time the sheet opens.
   *
   * On `open` rather than once, so cancelling and reopening starts from the top instead
   * of showing whatever was highlighted last time — the old dine-in modal did the same.
   *
   * ⚠️ **Keyed on `open` ALONE, never on `options` changing.** A caller may bind
   * `[options]` to a method — order-details does, `getDineInPaymentOptions()` — which
   * returns a new array of new object literals on every change-detection cycle. Angular
   * compares inputs by reference, so `options` reads as changed on every pass, including
   * the one a click triggers. Resetting on that made Card and Online payment impossible
   * to select: the choice was undone before the frame painted.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && changes['open'].currentValue === true) {
      this.selected = this.options[0]?.method ?? '';
    }
  }

  /**
   * The commit button's label for whatever is currently selected.
   *
   * ⚠️ This is why the order screen's button reads "Continue to payment" for Pay now:
   * that tap does not finish the order, it hands the guest to BORICA's card form.
   * Telling them they have ordered at that moment is a lie they only find out about if
   * the payment then fails.
   */
  get commitLabelKey(): string {
    const option = this.options.find((o) => o.method === this.selected);
    return option?.commitLabelKey ?? this.confirmLabelKey;
  }

  /** Artwork variant: the tap is the choice. */
  onChoose(option: PaymentSheetOption): void {
    if (this.busy) {
      return;
    }

    this.choose.emit(option.method);
  }

  /** Rows variant: highlight only. Nothing is ordered until confirm. */
  onSelect(option: PaymentSheetOption): void {
    if (this.busy) {
      return;
    }

    if (this.selected === option.method) {
      return;
    }

    this.selected = option.method;
    this.selectedChange.emit(option.method);
  }

  onConfirm(): void {
    if (this.busy || !this.selected) {
      return;
    }

    this.choose.emit(this.selected);
  }

  onDismiss(): void {
    this.dismiss.emit();
  }
}
